import type { GetStaticPropsContext } from 'next';
import { z } from 'zod';

import { Flex } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ShapesBanner } from '~/components/shapes-banner';
import { SignUpButtonLink } from '~/components/sign-up-button-link';
import { Testimonials } from '~/components/testimonials';
import { ThirdPartyReviews } from '~/components/third-party-reviews';
import { PageCategory } from '~/features/analytics/constants';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { getCompanies } from '~/features/companies/controllers/get-companies';
import { CompanyContentList } from '~/features/companies/models/company';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { parsePageContentData } from '~/features/page/utils/parse-page-content-data';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { EnterpriseContentBlockSectionWithLinks } from '~/features/page-template/templates/home/components/enterprise-section';
import { HomeTemplate, homeTemplateQuery, homeTemplatesSchema } from '~/features/page-template/templates/home/model';
import { IndividualPlansSectionV2 } from '~/features/plans/components/individual-plans-section-v2';
import { LogoGardenSection } from '~/features/plans/components/logo-garden';
import { purchaseFaqSchema, PurchaseFaqSection } from '~/features/plans/components/plans-faq-section';
import { PlansMatrix, plansMatrixSchema } from '~/features/plans/components/plans-matrix';
import { Stats, statsSchema } from '~/features/plans/components/stats';
import { planMetadataSchema } from '~/features/plans/models/plan-metadata';
import { getRelatedPrograms } from '~/features/program/controllers/get-related-programs';
import { getAllThirdPartyReviews } from '~/features/third-party-reviews/controller';
import { ThirdPartyReviewContentList } from '~/features/third-party-reviews/model';

const logoGardenSchema = z.object({
  title: z.string(),
  companies: z.array(z.string()),
});
const hardcodedTestimonialsSchema = z.array(
  z.object({
    _id: z.string(),
    _type: z.literal('testimonial'),
    quote: z.string(),
    glyphName: z.string(),
    name: z.string(),
  })
);

const pageContentDataSchema = z.object({
  pageTitle: z.string(),
  teamPlan: z.string().transform((value) => planMetadataSchema.parse(JSON.parse(value))),
  faq: z.string().transform((value) => purchaseFaqSchema.parse(JSON.parse(value))),
  stats: z.string().transform((value) => statsSchema.parse(JSON.parse(value))),
  logoGarden: z.string().transform((value) => logoGardenSchema.parse(JSON.parse(value))),
  testimonials: z.string().transform((value) => hardcodedTestimonialsSchema.parse(JSON.parse(value))),
  plansMatrix: z.string().transform((value) => plansMatrixSchema.parse(JSON.parse(value))),
});

type PageContentData = z.infer<typeof pageContentDataSchema>;

export default function Plans({
  homePageData,
  page,
  pageContentData,
  // Note: this is a temp solution to get the logos on the page.
  companies,
  thirdPartyReviews,
  relatedPrograms,
}: {
  page: Page;
  pageContentData: PageContentData;
  homePageData: HomeTemplate;
  companies: CompanyContentList;
  thirdPartyReviews: ThirdPartyReviewContentList;
  relatedPrograms: CatalogItem[];
}) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Plans} />
      <PageLayout withoutVerticalPadding>
        <Flex direction={'column'} gap={2} marginBottom={2}>
          <IndividualPlansSectionV2
            buttonTheme='lime'
            footerSlot={<EnterpriseContentBlockSectionWithLinks homePageData={homePageData} />}
            relatedPrograms={relatedPrograms}
            showTeamCard={true}
            teamPlanMetadata={pageContentData.teamPlan}
            title={pageContentData.pageTitle}
          />
          <PlansMatrix {...pageContentData.plansMatrix} />
        </Flex>
        <LogoGardenSection
          logos={companies}
          theme='dark'
          title={pageContentData.logoGarden.title}
          logoContainerProps={{
            maxWidth: '746px',
            margin: '0 auto',
          }}
        />
        <Testimonials
          testimonials={pageContentData.testimonials}
          title='Learners Love Udacity'
          footerContent={
            <ThirdPartyReviews
              reviews={thirdPartyReviews}
              customLinkLabel={(review) => (
                <>
                  {review.reviewCount} Reviews <ArrowRightIcon height='20px' width='20px' />
                </>
              )}
            />
          }
        />
        <Stats {...pageContentData.stats} />
        <PurchaseFaqSection {...pageContentData.faq} />
        <ShapesBanner
          title='Start Learning for Free Today'
          buttonSlot={
            <SignUpButtonLink
              colorScheme='sea-foam'
              rightIcon={<ArrowRightIcon h='24px' w='24px' />}
              size='icon-right'
              width={{ base: '100%', md: 'fit-content' }}
            >
              Sign Up Today
            </SignUpButtonLink>
          }
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, homePageData, thirdPartyReviews] = await Promise.all([
    getPage('/plans'),
    getPageTemplate<HomeTemplate>({
      templateName: 'Home Page',
      templateQuery: homeTemplateQuery,
      templateZodSchema: homeTemplatesSchema,
    }),
    getAllThirdPartyReviews(),
  ]);

  const pageContentData = parsePageContentData<PageContentData>(page.pageContentData, pageContentDataSchema);
  const companies = await getCompanies(pageContentData.logoGarden.companies);

  return {
    props: {
      page,
      homePageData,
      pageContentData,
      companies,
      thirdPartyReviews,
      relatedPrograms: getRelatedPrograms(),
    },
  };
}
